import React, { useCallback, useMemo, type PropsWithChildren, type SyntheticEvent } from 'react';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useEffectWithoutConcurrency } from '@atlassian/jira-react-strict-mode/src/useEffectWithoutConcurrency.tsx';
import startWithTemplateSpotlightImage from './start-with-template-spotlight.png';
import { useDelayedEligibility } from './useDelayedEligibility.tsx';
import { START_WITH_TEMPLATE_SPOTLIGHT } from '@jira/polaris__lib-onboarding-flows-store/src/cardCoversSpotlight.constants.tsx';
import { useOnboardingFlowsActions } from '@jira/polaris__lib-onboarding-flows-store/src/useOnboardingFlowsActions.tsx';
import { useProductUsageStartTimestamp } from '@jira/polaris__lib-onboarding-flows-store/src/useProductUsageStartTimestamp.tsx';
import {
	OnboardingSpotlightFeatureIds,
	PO_HEADLESS_MESSAGE_TEMPLATE_ID,
	PO_HEADLESS_PLACEMENT_ID,
} from './onboardingConstants.tsx';
import { usePolarisHeadlessSpotlightMessage } from './usePolarisHeadlessSpotlightMessage.tsx';
import { checkIfOlderThanNDays } from './checkIfOlderThanNDays.tsx';
import { SpotlightTarget } from '@atlassian/jira-spotlight/src/ui/SpotlightTarget.tsx';

const spotlightHeading = defineMessage({
	id: 'polaris.lib-onboarding-flows.start-with-template-spotlight.heading',
	defaultMessage: 'Start with a template',
	description: 'Heading for the Start with a template spotlight',
});

const spotlightDescription = defineMessage({
	id: 'polaris.lib-onboarding-flows.start-with-template-spotlight.description',
	defaultMessage: 'Create a view based on popular prioritization frameworks.',
	description: 'Description for the Start with a template spotlight',
});

const spotlightTryItNowButton = defineMessage({
	id: 'polaris.lib-onboarding-flows.start-with-template-spotlight.try-it-now',
	defaultMessage: 'Try it now',
	description: 'Primary action button for the Start with a template spotlight',
});

const DISMISS_CLICK_IDENTIFIER_BY_ACTION = {
	tryItNow: 'try-it-now',
	dismiss: 'dismiss',
	close: 'close',
} as const;
const PO_START_WITH_TEMPLATE_SPOTLIGHT_DELAY_MS = 3000;

type StartWithTemplateSpotlightProps = PropsWithChildren<{
	onTryItNow?: () => void;
	spotlightTargetNode?: HTMLElement | null;
	isDelayEligible?: boolean;
}>;

export const StartWithTemplateSpotlight = ({
	children,
	onTryItNow,
	spotlightTargetNode,
	isDelayEligible = true,
}: StartWithTemplateSpotlightProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { loadProductUsageStartTimestamp } = useOnboardingFlowsActions();
	const productUsageStartTimestamp = useProductUsageStartTimestamp();
	const { isPoMessageVisible, dismiss, renderJiraSpotlight } = usePolarisHeadlessSpotlightMessage({
		placementId: PO_HEADLESS_PLACEMENT_ID,
		messageTemplateId: PO_HEADLESS_MESSAGE_TEMPLATE_ID,
		featureId: OnboardingSpotlightFeatureIds.START_WITH_TEMPLATE,
		clickIdentifiers: {
			dismiss: 'close',
			primary: 'try-it-now',
		},
	});

	useEffectWithoutConcurrency(() => {
		loadProductUsageStartTimestamp();
	}, [loadProductUsageStartTimestamp]);

	const isNewCustomer =
		Boolean(productUsageStartTimestamp) && !checkIfOlderThanNDays(productUsageStartTimestamp, 7);
	const isSpotlightEligible = isDelayEligible && isNewCustomer && isPoMessageVisible;
	const isSpotlightVisible = useDelayedEligibility({
		isEligible: isSpotlightEligible,
		delayMs: PO_START_WITH_TEMPLATE_SPOTLIGHT_DELAY_MS,
	});
	const dismissSpotlight = useCallback(
		(clickIdentifier: 'dismiss' | 'try-it-now' | 'close') => {
			dismiss(clickIdentifier === 'try-it-now' ? 'primary' : 'dismiss');
		},
		[dismiss],
	);

	const handleTryItNow = useCallback(
		(_event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'startWithTemplateSpotlightTryItNow');
			dismissSpotlight(DISMISS_CLICK_IDENTIFIER_BY_ACTION.tryItNow);
			onTryItNow?.();
		},
		[onTryItNow, dismissSpotlight],
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onCloseButtonDismiss = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({ action: 'dismissed', actionSubject: 'jpdSpotlight' }), {
			activeSpotlight: 'startWithTemplateSpotlight',
		});
		dismissSpotlight(DISMISS_CLICK_IDENTIFIER_BY_ACTION.close);
	}, [createAnalyticsEvent, dismissSpotlight]);

	const actions = useMemo(() => {
		return [
			{
				onClick: handleTryItNow,
				text: formatMessage(spotlightTryItNowButton),
				ctaType: 'primary' as const,
				testId: 'polaris.lib-onboarding-flows.start-with-template-spotlight.try-it-now-button',
			},
		];
	}, [handleTryItNow, formatMessage]);

	if (!isSpotlightVisible) {
		return <>{children}</>;
	}

	const spotlightContent = renderJiraSpotlight({
		actions,
		dialogPlacement: 'bottom left',
		target: START_WITH_TEMPLATE_SPOTLIGHT,
		targetNode: spotlightTargetNode ?? undefined,
		heading: formatMessage(spotlightHeading),
		testId: 'lib-onboarding-flows.start-with-template-spotlight',
		image: startWithTemplateSpotlightImage,
		shouldWatchTarget: true,
		shouldObserveRootForTarget: true,
		onDismiss: onCloseButtonDismiss,
		children: formatMessage(spotlightDescription),
		fallbackMessageId: PO_HEADLESS_MESSAGE_TEMPLATE_ID,
	});

	return (
		<>
			<SpotlightTarget name={START_WITH_TEMPLATE_SPOTLIGHT}>{children}</SpotlightTarget>
			{spotlightContent}
		</>
	);
};
